import {
  CreateDrawer,
  makeNavGuard,
  useDirtyClose,
  useStepAttention,
} from '../dashboards/primitives';
import { DrawerScrim } from '../dashboards/primitives/DrawerScrim';

import { buildPrivateEventSteps } from './createPrivateEventSteps';
import { usePrivateEventForm } from './usePrivateEventForm';

const DISCARD_PROMPT = 'Discard this private event? Your changes won’t be saved.';

/**
 * Create a private event, on the same `CreateDrawer` primitive as the venue and
 * workshop flows. A shell: state lives in `usePrivateEventForm` and the fields
 * in `createPrivateEventSteps`. Continue is always clickable and validates the
 * step; Create validates every step and returns to the first that fails.
 */
export default function CreatePrivateEventDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const form = usePrivateEventForm(onCreated);
  const requestClose = useDirtyClose(form.dirty, onClose, DISCARD_PROMPT);
  const steps = buildPrivateEventSteps(form);
  const { attend, announcement } = useStepAttention();
  const guard = makeNavGuard(form);

  const attendStep = (index: number) =>
    attend(`Step ${index + 1} of ${steps.length}, ${steps[index].label}`);
  const onNavigate = (target: number, current: number) => {
    const allowed = guard(target, current);
    if (!allowed) attendStep(current);
    return allowed;
  };
  const onCta = () =>
    void form.submit().then((failing) => {
      if (failing !== null) attendStep(failing);
    });

  return (
    <DrawerScrim onScrimClick={requestClose}>
      <CreateDrawer
        title="New private event"
        eyebrow="New private event"
        steps={steps}
        step={form.step}
        onStepChange={form.setStep}
        ctaLabel={form.submitting ? 'Creating…' : 'Create private event'}
        continueAlwaysEnabled
        onNavigate={onNavigate}
        announcement={announcement}
        canContinue={!form.submitting}
        onCta={onCta}
        onCancel={requestClose}
        onClose={requestClose}
        footNote="never appears on the public site"
        style={{ height: '100%' }}
      />
    </DrawerScrim>
  );
}
